import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import Body, {
  Size as BodySize,
  LetterCase as BodyLetterCase,
  Spacing as BodySpacing,
} from 'components/Typography/Body';
import Spinner from 'components/Common/Spinner';
import Modal from 'components/Common/Modal';
import Confirmation from 'components/Common/Confirmation';
import Toast from 'components/Common/Toast';
import BrandSummary from 'components/Pages/Brands/BrandSummary';
import {
  selectAcceptBrandState,
  selectFetchPendingBrandsState,
  selectRejectBrandState,
} from 'state/selectors/brands';
import {
  acceptBrand,
  clearBrandsState,
  fetchPendingBrands,
  rejectBrand,
} from 'state/actions/brands';
import { selectAuthenticatedUserState } from 'state/selectors/auth';
import useModal from 'hooks/useModal';
import ModalType from 'enums/modal/modalType.enum';

import classes from './PendingBrands.module.scss';

const PendingBrands = () => {
  const dispatch = useDispatch();

  const { user } = useSelector(selectAuthenticatedUserState, shallowEqual);

  const {
    error: errorPendingBrands,
    success: successPendingBrands,
    loading: loadingPendingBrands,
    pendingBrands,
  } = useSelector(selectFetchPendingBrandsState, shallowEqual);

  const {
    error: errorAcceptBrand,
    success: successAcceptBrand,
    loading: loadingAcceptBrand,
  } = useSelector(selectAcceptBrandState, shallowEqual);

  const {
    error: errorRejectBrand,
    success: successRejectBrand,
    loading: loadingRejectBrand,
  } = useSelector(selectRejectBrandState, shallowEqual);

  const [brandsToShow, setBrandsToShow] = useState([]);
  const [brandIdToDelete, setBrandIdToDelete] = useState(null);

  const { modal, onOpenModalHandler, onCloseModalHandler } = useModal();

  useEffect(() => {
    if (user) dispatch(fetchPendingBrands(user.network));

    return () => {
      dispatch(clearBrandsState());
    };
  }, [user]);

  useEffect(() => {
    if (successPendingBrands) {
      setBrandsToShow(pendingBrands);
      dispatch(clearBrandsState());
    }
  }, [successPendingBrands, pendingBrands]);

  useEffect(() => {
    if (successAcceptBrand || successRejectBrand) {
      onCloseModalHandler();
      setBrandsToShow((prevState) =>
        prevState.filter((doc) => doc.uid !== brandIdToDelete)
      );
      setBrandIdToDelete(null);
    }
  }, [successAcceptBrand, successRejectBrand, brandIdToDelete]);

  const onClickAcceptHandler = useCallback((brand) => {
    onOpenModalHandler(ModalType.ACCEPT_BRAND, {
      brandId: brand.uid,
    });
  }, []);

  const onClickRejectHandler = useCallback((brand) => {
    onOpenModalHandler(ModalType.REJECT_BRAND, {
      brandId: brand.uid,
    });
  }, []);

  const onAcceptBrandHandler = useCallback(() => {
    setBrandIdToDelete(modal.brandId);
    dispatch(acceptBrand({ brandId: modal.brandId }));
  }, [modal]);

  const onRejectBrandHandler = useCallback(() => {
    setBrandIdToDelete(modal.brandId);
    dispatch(rejectBrand({ brandId: modal.brandId }));
  }, [modal]);

  return (
    <>
      {errorPendingBrands && (
        <Toast text={errorPendingBrands} id="Fetch brands error" />
      )}
      {errorRejectBrand && (
        <Toast text={errorRejectBrand} id="Reject brand error" />
      )}
      {errorAcceptBrand && (
        <Toast text={errorAcceptBrand} id="Accept brand error" />
      )}
      <Modal
        isOpen={modal.type === ModalType.ACCEPT_BRAND}
        onClose={onCloseModalHandler}
      >
        <Confirmation
          title="Accept brand"
          description="Are you sure you want to accept the selected brand?"
          onAccept={onAcceptBrandHandler}
          onCancel={onCloseModalHandler}
          loading={loadingAcceptBrand}
        />
      </Modal>
      <Modal
        isOpen={modal.type === ModalType.REJECT_BRAND}
        onClose={onCloseModalHandler}
      >
        <Confirmation
          title="Reject brand"
          description="Are you sure you want to reject the selected brand?"
          onAccept={onRejectBrandHandler}
          onCancel={onCloseModalHandler}
          loading={loadingRejectBrand}
          acceptButtonText="Yes"
        />
      </Modal>
      <div className={classes.container}>
        <div className={classes.content}>
          <Body
            letterCase={BodyLetterCase.Uppercase}
            spacing={BodySpacing.M}
            className={classes.title}
          >
            Brands for approval
          </Body>
          {loadingPendingBrands ? (
            <Spinner className={classes.spinner} />
          ) : (
            <>
              {brandsToShow.length === 0 ? (
                <Body
                  size={BodySize.XS}
                  spacing={BodySpacing.S}
                  className={classes.noBrands}
                >
                  There are no brands for approval
                </Body>
              ) : (
                <>
                  <div className={classes.brands}>
                    {brandsToShow.map((brand, index) => {
                      const key = `brand-${index}`;
                      return (
                        <BrandSummary
                          key={key}
                          brand={brand}
                          onAccept={() => onClickAcceptHandler(brand)}
                          onReject={() => onClickRejectHandler(brand)}
                          isPending
                        />
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PendingBrands;
